import { Link, useLocation } from "react-router-dom";
import { ChevronRight, Home } from "lucide-react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

// Map paths to readable labels
const pathLabels: Record<string, string> = {
  "": "Dashboard",
  newsletters: "Newsletters",
  content: "Content",
  users: "Users",
  analytics: "Analytics",
  settings: "Settings",
  profile: "Profile",
  login: "Login",
  new: "New",
  edit: "Edit",
};

interface BreadcrumbItem {
  label: string;
  path: string;
  isLast: boolean;
}

export function Breadcrumbs() {
  const location = useLocation();
  const pathSegments = location.pathname.split("/").filter(Boolean);

  // Build breadcrumb items
  const breadcrumbs: BreadcrumbItem[] = pathSegments.map((segment, index) => {
    const path = "/" + pathSegments.slice(0, index + 1).join("/");
    const isLast = index === pathSegments.length - 1;

    // Check if this is an ID (UUID or numeric)
    const isId = /^[0-9a-f-]{36}$|^\d+$/.test(segment);
    const label = isId ? "Details" : pathLabels[segment] || capitalizeFirst(segment);

    return { label, path, isLast };
  });

  // Add home as first item if we're not on home
  if (pathSegments.length > 0) {
    breadcrumbs.unshift({
      label: "Dashboard",
      path: "/",
      isLast: false,
    });
  }

  return (
    <nav aria-label="Breadcrumb" className="flex items-center">
      <ol className="flex items-center gap-1">
        {breadcrumbs.length === 0 ? (
          <li className="flex items-center gap-2">
            <Home className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Dashboard</span>
          </li>
        ) : (
          breadcrumbs.map((item, index) => (
            <motion.li
              key={item.path}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              className="flex items-center"
            >
              {index > 0 && (
                <ChevronRight className="h-4 w-4 mx-1 text-muted-foreground/50" />
              )}
              {item.isLast ? (
                <span className="text-sm font-medium text-foreground">
                  {item.label}
                </span>
              ) : (
                <Link
                  to={item.path}
                  className={cn(
                    "text-sm text-muted-foreground transition-colors duration-200",
                    "hover:text-foreground",
                    index === 0 && "flex items-center gap-1.5"
                  )}
                >
                  {index === 0 && <Home className="h-3.5 w-3.5" />}
                  <span>{item.label}</span>
                </Link>
              )}
            </motion.li>
          ))
        )}
      </ol>
    </nav>
  );
}

function capitalizeFirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1).replace(/-/g, " ");
}
